import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, CircleCheck, CircleHelp, Copy, ShieldAlert, TriangleAlert } from 'lucide-react'
import Button from './Button'
import TextField from './TextField'
import useDialog from './useDialog'
import { kit } from './strings'
import './ConfirmDialog.css'

/**
 * What each tone looks like. The default is the delete it was built for; the others exist
 * because this dialog is ALSO how the panel asks about activating, stopping and resending,
 * and every one of those used to open under a red warning triangle titled "Confirm delete"
 * over a sentence about something else entirely.
 */
const TONES = {
  danger: { icon: TriangleAlert, variant: 'danger' },
  warning: { icon: TriangleAlert, variant: 'warning' },
  success: { icon: CircleCheck, variant: 'success' },
  primary: { icon: CircleHelp, variant: 'primary' },
}

/**
 * Destructive confirmation — the legacy SweetAlert2 delete prompt
 * (`Views/Groups/js.cshtml:513-524`): error icon, body text, no title, a red confirm
 * and a neutral cancel. Under RTL SweetAlert puts confirm on the right, cancel on the
 * left; the same order is kept here.
 *
 * Defaults are the strings the legacy dialog uses — `حذف` (Groups.btnDelete) and
 * `إغلاق` (Groups.btnClose).
 */
export default function ConfirmDialog({
  open,
  title = kit.confirmDelete,
  message,
  /**
   * WHAT KIND OF QUESTION THIS IS — `danger` (the default) for a delete, `warning` for
   * something reversible like stopping or suspending, `success` for turning something on,
   * `primary` for a plain "shall I?". It picks the icon and the colour of the confirm
   * button, so an activate no longer asks in the red of an irreversible delete.
   */
  tone = 'danger',
  confirmLabel = kit.delete,
  cancelLabel = kit.close,
  /**
   * TYPE-TO-CONFIRM. Pass the exact text the reader must copy out — a programme's name, a
   * topic's — and the dialog will not let the delete run until it has been typed back.
   *
   * For a delete that takes other people's work with it, a single click is too cheap. The
   * phrase is shown selectable with a copy button beside it, so confirming is deliberate
   * without being a memory test.
   */
  confirmPhrase,
  confirmPhraseLabel = kit.confirmByTyping,
  /**
   * THE ACT IS RUNNING. Only for a caller that already tracks its own pending
   * state — the dialog works this out by itself otherwise, by awaiting
   * `onConfirm`. See the note on `run` below.
   */
  busy = false,
  onConfirm,
  onCancel,
}) {
  const { icon: ToneIcon, variant } = TONES[tone] ?? TONES.danger
  const messageId = useId()
  const typedId = useId()
  const [typed, setTyped] = useState('')
  const [copied, setCopied] = useState(false)
  const [running, setRunning] = useState(false)

  // A fresh dialog never opens onto the last one's answer.
  useEffect(() => {
    if (!open) return
    setTyped('')
    setCopied(false)
    setRunning(false)
  }, [open])

  const matches = !confirmPhrase || typed.trim() === String(confirmPhrase).trim()

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(confirmPhrase))
      setCopied(true)
    } catch {
      /* A refused clipboard is not worth a message: the name is on screen to be typed. */
    }
  }
  const confirmRef = useRef(null)

  /**
   * ONE DELETE PER PRESS.
   *
   * The confirm button was a plain `onClick={onConfirm}` and every caller's
   * handler is async: press it twice and the DELETE went twice. On a row that
   * had already gone the second call answered 404 and the page reported a
   * failure for something that had in fact just worked.
   *
   * The dialog awaits the handler itself rather than asking 25 call sites to
   * thread a pending flag through. While it runs, the button is busy and cannot
   * be pressed again; when it settles the button comes BACK, because the
   * contract here is that a failed act leaves the dialog open — every caller
   * ends its handler with `if (!outcome.ok) return toast.error(…)` and only
   * closes on success. A one-shot latch would have made a retry impossible.
   */
  const run = async () => {
    if (running || busy || !matches) return
    setRunning(true)
    try {
      await onConfirm?.()
    } finally {
      setRunning(false)
    }
  }

  /* Scroll lock, Escape, the Tab trap, focus restore and the exit transition —
     shared with every other dialog. The trap is the fix that matters here: this
     is an alertdialog, and Tab used to walk straight out of it into the page
     behind, where the row's own delete button was still sitting focusable. */
  /* With a phrase to type, the reader starts in the field: the confirm button stays disabled
     until the phrase matches, and a disabled button cannot take focus — so focus stayed on
     the page behind the dialog. The field's input is looked up when focus is due. */
  const initialFocus = useMemo(
    () => (confirmPhrase ? { get current() { return document.getElementById(typedId) } } : confirmRef),
    [confirmPhrase, typedId],
  )
  const { mounted, state, ref } = useDialog({ open, onClose: onCancel, initialFocus })

  if (!mounted) return null

  /* To <body>, like every other overlay in the kit. A `position: fixed` box is
     anchored by the nearest ancestor carrying a transform, a filter or
     containment — and the page column has all three at various moments (the
     route-entrance animation, container queries). Portalling puts the dialog
     above that whole question. */
  return createPortal(
    <div
      className={`confirm-dialog${state === 'leaving' ? ' is-leaving' : ''}`}
      /* The tone on the ROOT, not just on the mark. Everything tone-coloured in
         the dialog — the mark's rings, the confirm button's focus halo — reads
         one variable from here, so they cannot disagree about what kind of
         question this is. See --cd-tone in ConfirmDialog.css. */
      data-tone={tone}
      data-testid="confirm-dialog"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={messageId}
      /* On the way out it is a picture of a dialog, not a dialog: nothing in it
         should take a click or a screen reader's attention. */
      {...(state === 'leaving' ? { inert: true, 'aria-hidden': 'true' } : null)}
    >
      <div className="confirm-dialog__box" ref={ref}>
        <div className="confirm-dialog__head">
          <span className="confirm-dialog__icon" aria-hidden="true">
            <ToneIcon size={20} strokeWidth={1.9} />
          </span>
          {/* A title names the act; the message says what it will happen to.
              Without one the dialog opened straight into a sentence, which is
              the only surface in the app that did. */}
          <h2 className="confirm-dialog__title">
            <bdi>{title}</bdi>
          </h2>
        </div>
        <p id={messageId} className="confirm-dialog__message">
          <bdi>{message}</bdi>
        </p>

        {confirmPhrase && (
          <div className={`confirm-dialog__gate${matches ? ' is-matched' : ''}`}>
            <p className="confirm-dialog__gate-ask">
              <ShieldAlert size={15} strokeWidth={1.9} aria-hidden="true" />
              <bdi>{confirmPhraseLabel}</bdi>
            </p>

            {/* The name, to be copied rather than remembered. `user-select: all` in the
                stylesheet means one click takes the whole thing, which is what somebody
                without a clipboard permission will reach for. */}
            <p className="confirm-dialog__phrase">
              <bdi className="confirm-dialog__phrase-text">{confirmPhrase}</bdi>
              <button
                type="button"
                className={`confirm-dialog__copy${copied ? ' is-copied' : ''}`}
                onClick={copy}
              >
                {copied ? <Check size={13} strokeWidth={2.6} /> : <Copy size={13} strokeWidth={1.9} />}
                <bdi>{copied ? kit.copied : kit.copy}</bdi>
              </button>
            </p>

            {/* The kit's own field, not a bare input — this dialog should not be the one
                surface in the panel with a text box of its own design. */}
            <TextField
              id={typedId}
              value={typed}
              onChange={setTyped}
              placeholder={confirmPhrase}
              autoComplete="off"
              aria-label={confirmPhraseLabel}
              trailing={
                matches ? (
                  <span className="confirm-dialog__ok" aria-hidden="true">
                    <Check size={15} strokeWidth={2.6} />
                  </span>
                ) : null
              }
            />
          </div>
        )}

        <div className="confirm-dialog__actions">
          {/* The reference lets the delete be pressed and then scolds you for mistyping.
              Refusing the press instead says the same thing before the mistake. */}
          <Button
            ref={confirmRef}
            variant={variant}
            disabled={!matches}
            busy={busy || running}
            onClick={run}
            data-testid="confirm-accept"
          >
            {confirmLabel}
          </Button>
          {/* Cancel stays live while the act runs: a slow delete must still be
              abandonable, and closing is what every caller's error path does. */}
          <Button variant="ghost" onClick={onCancel} data-testid="confirm-cancel">
            {cancelLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
